import { onCLS, onFCP, onLCP, onTTFB, onINP, type Metric } from 'web-vitals';
import { captureMessage } from './monitoring';

/**
 * Web Vitals thresholds (Google recommendations)
 */
const THRESHOLDS = {
  // Cumulative Layout Shift: measures visual stability
  CLS: { good: 0.1, needsImprovement: 0.25 },
  
  // Interaction to Next Paint: measures responsiveness
  INP: { good: 200, needsImprovement: 500 },
  
  // Largest Contentful Paint: measures loading performance
  LCP: { good: 2500, needsImprovement: 4000 },
  
  // First Contentful Paint: measures perceived load speed
  FCP: { good: 1800, needsImprovement: 3000 },
  
  // Time to First Byte: measures server responsiveness
  TTFB: { good: 800, needsImprovement: 1800 },
};

/**
 * Determine if a metric value is good, needs improvement, or poor
 */
const getRating = (name: string, value: number): 'good' | 'needs-improvement' | 'poor' => {
  const threshold = THRESHOLDS[name as keyof typeof THRESHOLDS];
  if (!threshold) return 'good';
  
  if (value <= threshold.good) return 'good';
  if (value <= threshold.needsImprovement) return 'needs-improvement';
  return 'poor';
};

/**
 * Send metric to analytics/monitoring service
 */
const sendToAnalytics = (metric: Metric) => {
  const { name, value, rating, delta, id } = metric;
  const customRating = getRating(name, value);
  
  // Log to console in development
  if (import.meta.env.DEV) {
    const emoji = customRating === 'good' ? '✅' : customRating === 'needs-improvement' ? '⚠️' : '❌';
    console.log(`${emoji} [Web Vitals] ${name}:`, {
      value: Math.round(value),
      rating: customRating,
      delta: Math.round(delta),
      id,
    });
  }

  // Send to monitoring service in production
  if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN) {
    // Report poor metrics to Sentry
    if (customRating === 'poor') {
      captureMessage(`Poor Web Vital: ${name}`, 'warning');
    }
    
    // You can also send to Google Analytics, custom analytics, etc.
    if (window.gtag) {
      window.gtag('event', name, {
        event_category: 'Web Vitals',
        value: Math.round(value),
        event_label: id,
        non_interaction: true,
      });
    }
  }

  // Custom analytics endpoint (if you have one)
  if (import.meta.env.VITE_ANALYTICS_ENDPOINT) {
    navigator.sendBeacon(
      import.meta.env.VITE_ANALYTICS_ENDPOINT,
      JSON.stringify({
        metric: name,
        value: Math.round(value),
        rating: customRating,
        url: window.location.href,
        timestamp: Date.now(),
      })
    );
  }
};

/**
 * Initialize Web Vitals monitoring
 * Tracks Core Web Vitals and reports to configured analytics services
 */
export const initWebVitals = () => {
  try {
    // Core Web Vitals
    onCLS(sendToAnalytics);   // Cumulative Layout Shift
    onINP(sendToAnalytics);   // Interaction to Next Paint
    onLCP(sendToAnalytics);   // Largest Contentful Paint
    
    // Additional useful metrics
    onFCP(sendToAnalytics);   // First Contentful Paint
    onTTFB(sendToAnalytics);  // Time to First Byte

    if (import.meta.env.DEV) {
      console.info('✅ Web Vitals monitoring initialized');
    }
  } catch (error) {
    console.error('❌ Failed to initialize Web Vitals:', error);
  }
};

/**
 * Get current performance metrics
 * Useful for debugging or displaying in admin panels
 */
export const getPerformanceMetrics = () => {
  if (!window.performance) return null;

  const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
  const paint = performance.getEntriesByType('paint');

  return {
    // Navigation timing
    dns: navigation ? Math.round(navigation.domainLookupEnd - navigation.domainLookupStart) : 0,
    tcp: navigation ? Math.round(navigation.connectEnd - navigation.connectStart) : 0,
    ttfb: navigation ? Math.round(navigation.responseStart - navigation.requestStart) : 0,
    download: navigation ? Math.round(navigation.responseEnd - navigation.responseStart) : 0,
    domInteractive: navigation ? Math.round(navigation.domInteractive - navigation.fetchStart) : 0,
    domComplete: navigation ? Math.round(navigation.domComplete - navigation.fetchStart) : 0,
    loadComplete: navigation ? Math.round(navigation.loadEventEnd - navigation.fetchStart) : 0,
    
    // Paint timing
    fcp: paint.find(p => p.name === 'first-contentful-paint')?.startTime || 0,
    lcp: paint.find(p => p.name === 'largest-contentful-paint')?.startTime || 0,
  };
};

// Type augmentation for gtag
declare global {
  interface Window {
    gtag?: (command: string, ...args: any[]) => void;
  }
}
